import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  MoreVertical,
  Edit,
  Eye,
  Lock,
  Unlock,
  Trash2,
  CreditCard,
  MessageCircle,
} from 'lucide-react';
import { linkWhatsApp } from '@/utils/telefone';
import type { User } from '@/types/user.types';

interface UserActionsMenuProps {
  user: User;
  onViewDetails?: (id: string) => void;
  onEdit?: (id: string) => void;
  onBlock?: (id: string) => void;
  onUnblock?: (id: string) => void;
  onDelete?: (id: string) => void;
  onManageSubscription?: (id: string) => void;
}

const menuItemClass =
  'flex w-full items-center gap-3 whitespace-nowrap px-4 py-2.5 text-left text-sm text-text-body transition-colors hover:bg-muted focus:outline-none focus-visible:bg-muted';

// Kebab (⋮) + menu flutuante de ações do usuário. Portalizado + reposicionado
// pra não ser cortado por overflow — compartilhado pela linha (desktop) e card (mobile).
export function UserActionsMenu({
  user,
  onViewDetails,
  onEdit,
  onBlock,
  onUnblock,
  onDelete,
  onManageSubscription,
}: UserActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224;
      const menuHeight = 260;
      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;
      if (top + menuHeight > window.innerHeight) top = rect.top - menuHeight - 4;
      if (left < 8) left = 8;
      setPos({ top, left });
    }
    setOpen((v) => !v);
  };

  const isBlocked = user.status === 'blocked' || user.status === 'suspended';
  const whatsapp = linkWhatsApp(user.phone);

  const run = (fn?: (id: string) => void) => {
    fn?.(user.id);
    setOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={toggle}
        aria-label="Ações do usuário"
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded-full p-2 text-text-secondary transition-colors hover:bg-muted hover:text-text-title focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <MoreVertical size={18} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 w-56 overflow-hidden rounded-xl border border-border bg-bg-elevated py-1 shadow-elevated animate-fade-up"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            <button role="menuitem" onClick={() => run(onViewDetails)} className={menuItemClass}>
              <Eye size={16} className="text-text-secondary" />
              Ver detalhes
            </button>
            {whatsapp && (
              <a
                role="menuitem"
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={menuItemClass}
              >
                <MessageCircle size={16} className="text-success" />
                Mensagem no WhatsApp
              </a>
            )}
            <button role="menuitem" onClick={() => run(onEdit)} className={menuItemClass}>
              <Edit size={16} className="text-text-secondary" />
              Editar
            </button>
            {user.isProducer && (
              <button
                role="menuitem"
                onClick={() => run(onManageSubscription)}
                className={menuItemClass}
              >
                <CreditCard size={16} className="text-primary" />
                Gerenciar assinatura
              </button>
            )}
            {isBlocked ? (
              <button role="menuitem" onClick={() => run(onUnblock)} className={menuItemClass}>
                <Unlock size={16} className="text-success" />
                Desbloquear
              </button>
            ) : (
              <button role="menuitem" onClick={() => run(onBlock)} className={menuItemClass}>
                <Lock size={16} className="text-warning" />
                Bloquear
              </button>
            )}
            <div className="my-1 border-t border-border" />
            <button
              role="menuitem"
              onClick={() => run(onDelete)}
              className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2.5 text-left text-sm font-medium text-error transition-colors hover:bg-error/10 focus:outline-none focus-visible:bg-error/10"
            >
              <Trash2 size={16} />
              Excluir
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}
